import React from 'react';
import { Eye, Users, MessageSquare, FileText, DollarSign, Package, Calendar, ShoppingCart } from 'lucide-react';

export const AdminDashboard = ({ analytics, stats, setActiveTab, setResourcesSubTab, setProductsSubTab }) => {
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Total Visitors</p>
              <p className="text-3xl font-bold">{analytics.total_visitors || 0}</p>
            </div>
            <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center">
              <Eye className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Active Users</p>
              <p className="text-3xl font-bold text-slate-900">{analytics.active_users || 0}</p>
              <p className="text-xs text-slate-500">Last 30 days</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <Users className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Forum Users</p>
              <p className="text-3xl font-bold text-slate-900">{analytics.forum_users || 0}</p>
              <p className="text-xs text-slate-500">Posted at least once</p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
              <MessageSquare className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Blog Posts</p>
              <p className="text-3xl font-bold text-slate-900">{analytics.approved_blog_posts || stats.totalBlogPosts}</p>
              <p className="text-xs text-slate-500">Published</p>
            </div>
            <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
              <FileText className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-[#8B1818] to-[#701313] rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Total Revenue</p>
              <p className="text-3xl font-bold">&#8369;{(analytics.total_revenue || 0).toLocaleString()}</p>
            </div>
            <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-slate-900">{stats.totalUsers}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Products</p>
              <p className="text-3xl font-bold text-slate-900">{stats.totalProducts}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <Package className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Bookings</p>
              <p className="text-3xl font-bold text-slate-900">{stats.totalBookings}</p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Orders</p>
              <p className="text-3xl font-bold text-slate-900">{stats.totalOrders}</p>
            </div>
            <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
              <ShoppingCart className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => { setActiveTab('resources-group'); setResourcesSubTab('blog'); }}
            className="p-4 border-2 border-slate-200 rounded-lg hover:border-[#8B1818] hover:bg-slate-50 transition-colors text-left"
            data-testid="quick-action-blog"
          >
            <FileText className="h-8 w-8 text-[#8B1818] mb-2" />
            <p className="font-semibold text-slate-900">Create Blog Post</p>
            <p className="text-sm text-slate-600">Write a new article</p>
          </button>
          <button
            onClick={() => { setActiveTab('products-group'); setProductsSubTab('add-products'); }}
            className="p-4 border-2 border-slate-200 rounded-lg hover:border-[#8B1818] hover:bg-slate-50 transition-colors text-left"
            data-testid="quick-action-product"
          >
            <Package className="h-8 w-8 text-[#8B1818] mb-2" />
            <p className="font-semibold text-slate-900">Add Product</p>
            <p className="text-sm text-slate-600">List a new eBook or souvenir</p>
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className="p-4 border-2 border-slate-200 rounded-lg hover:border-[#8B1818] hover:bg-slate-50 transition-colors text-left"
            data-testid="quick-action-users"
          >
            <Users className="h-8 w-8 text-[#8B1818] mb-2" />
            <p className="font-semibold text-slate-900">Manage Users</p>
            <p className="text-sm text-slate-600">View all registered users</p>
          </button>
          <button
            onClick={() => { setActiveTab('products-group'); setProductsSubTab('orders'); }}
            className="p-4 border-2 border-slate-200 rounded-lg hover:border-[#8B1818] hover:bg-slate-50 transition-colors text-left relative"
            data-testid="quick-action-orders"
          >
            {stats.pendingOrders > 0 && (
              <span className="absolute top-2 right-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                {stats.pendingOrders}
              </span>
            )}
            <ShoppingCart className="h-8 w-8 text-[#8B1818] mb-2" />
            <p className="font-semibold text-slate-900">Manage Orders</p>
            <p className="text-sm text-slate-600">Review and approve payments</p>
          </button>
        </div>
      </div>
    </div>
  );
};
